import angular, { IPromise } from 'angular'
import moment from 'moment'
import { add, round, vn } from 'vend-number'
import { ApiRegister } from '@vend/types'

import defaults from 'lodash/defaults'
import includes from 'lodash/includes'

import { trackError } from '@vend/utilities'
import { CASH_MOVEMENT_TYPES, EVENT } from '../constants'
import { $log, $q, $rootScope } from '../ng-compat/imports'
import {
  registerManager,
  userManager,
  registersResource,
  usersStore,
} from '../ng-compat/managers'
const UNIQUE_TYPES = [
  CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT,
  CASH_MOVEMENT_TYPES.CLOSING_CASH_FLOAT,
]
const NON_PERMITTED_CASH_MOVEMENT_RECEIPTS = [
  CASH_MOVEMENT_TYPES.TILL_DISCREPANCY,
  CASH_MOVEMENT_TYPES.CLOSING_CASH_FLOAT,
  CASH_MOVEMENT_TYPES.FLOAT_ADJUSTMENT,
]

/**
 * Responsible for managing cash movements for cash management
 */

export class CashMovementsManager {
  /**
   * Return the payment type id that is being used for cash management.
   *
   * @return {String} The payment type id being used for cash management
   */
  public getCashManagementPaymentTypeId(): string | null {
    const activeRegister = registerManager.getActiveRegister()
    return activeRegister && activeRegister.cash_managed_payment_type_id
  }

  /**
   * Return the cash drawer totals, consisting of cash movements total and cash sales total. Also returns the
   * calculated cash drawer balance (cash movements + cash sales)
   *
   * @return {Promise<Object>} A promise resolved once the cash totals have been retrieved.
   */
  public getCashDrawerTotals(): IPromise<$TSFixMe> {
    return registersResource
      .getCashTotals(registerManager.getActiveRegister())
      .then((cashDrawerTotals: $TSFixMe) => {
        cashDrawerTotals.cashDrawerBalance = add(
          cashDrawerTotals.movements_total,
          cashDrawerTotals.cash_sales_total
        )

        return cashDrawerTotals
      })
  }

  /**
   * Determine if cash management is enabled for this register.
   *
   * @return {Boolean} True is cash management is enabled for this register, false otherwise.
   */
  public isCashManagementEnabled(): boolean {
    return Boolean(this.getCashManagementPaymentTypeId())
  }

  /**
   * Return the history of cash movements for the current register.
   *
   * @param configOptions {Object}
   *        Additional filters which can be passed to getCashMovements.
   *
   * @return {Promise<Array>} A promise resolved once the cash movements for the register have been retrieved.
   */
  public getCashMovements(configOptions?: $TSFixMe): IPromise<$TSFixMe[]> {
    const register = registerManager.getActiveRegister()
    const cashMovementsConfig = {
      register_open_sequence_id: register && register.register_open_sequence_id,
    }

    return registersResource
      .getCashMovements(defaults(cashMovementsConfig, configOptions))
      .then(cashMovements => userManager.hydrateUsers(cashMovements))
  }

  /**
   * Responsbile for constructing the cash movement payload to send to the api. Used for recording Cash in, Cash out,
   * Petty cash in and Petty cash out.  This will also emit the cash movement
   *
   * @param cashMovement {Object}
   *        The cash movement to record. Should consist of amount and note:
   *
   *        @param cashMovement.amount {String}
   *                The cash movement amount.
   *
   *        @param [cashMovement.note] {String}
   *                A note to accompany the cash movement.
   *
   * @param [register] {Register}
   *        The register to record a cash movement against.
   *
   * @return {Promise} A promise resolved when the cash movement has been recorded with the api.
   */
  public recordCashMovement(
    cashMovement: {
      amount: number | string
      note?: string
      type: CASH_MOVEMENT_TYPES
    },
    register?: ApiRegister
  ): IPromise<unknown> {
    const cashMovementRegister = register || registerManager.getActiveRegister()
    const cashMovementData = {
      amount: vn(round(cashMovement.amount, 2)).toNumber(),
      date: moment.utc().format(),
      user_id: userManager.getLoggedInUser().id,
      register_open_sequence_id: cashMovementRegister.register_open_sequence_id,
    }

    return registersResource
      .recordCashMovement(defaults(cashMovementData, cashMovement) as $TSFixMe)
      .then(cashMovement => {
        if (
          !includes(
            NON_PERMITTED_CASH_MOVEMENT_RECEIPTS,
            (cashMovementData as $TSFixMe).type
          )
        ) {
          usersStore
            .get(cashMovementData.user_id)
            .then(userData => {
              const { amount, date, type, note } = cashMovementData as $TSFixMe
              $rootScope.$emit(EVENT.PRINT, {
                type: 'cash-movement-receipt',
                data: {
                  amount,
                  date,
                  note,
                  type,
                  username: userData!.display_name,
                },
              })
            })
            // Fire-and-forget: suppress unhandled rejection (native Promises propagate
            // errors that $q previously caught silently via its internal try-catch)
            .catch(() => {})
        }

        return cashMovement
      })
      .catch((error: $TSFixMe) =>
        this._isDuplicateMovementRequest(cashMovementData, error)
      )
  }

  /**
   * Records a cash movement float adjustment. Occurs when the cash in till is less than the register's opening
   * float.
   *
   * @param {Number} floatAdjustmentAmount
   *        The amount of the adjustment.
   *
   * @return {Promise} Promise resolved when the float adjustment has been made.
   */
  public recordFloatAdjustment(
    floatAdjustmentAmount: number
  ): IPromise<unknown> {
    const cashMovementData = {
      amount: floatAdjustmentAmount,
      type: CASH_MOVEMENT_TYPES.FLOAT_ADJUSTMENT,
    }
    $log.debug(
      `[CASH MOVEMENTS MANAGER] Float adjustment: ${cashMovementData.amount}`
    )
    return this.recordCashMovement(cashMovementData)
  }

  /**
   * Records a cash movement till discrepancy. Occurs when the counted cash in till does not equal the expected
   * amount.
   *
   * @param {Number} tillDiscrepancyAmount
   *        The amount of the discrepancy
   *
   * @return {Promise} Promise resolved when the till discrepancy has been made.
   */
  public recordTillDiscrepancy(
    tillDiscrepancyAmount: number | string
  ): IPromise<unknown> {
    const cashMovementData = {
      amount: tillDiscrepancyAmount,
      type: CASH_MOVEMENT_TYPES.TILL_DISCREPANCY,
    }
    $log.debug(
      `[CASH MOVEMENTS MANAGER] Till discrepancy: ${cashMovementData.amount}`
    )
    return this.recordCashMovement(cashMovementData)
  }

  /**
   * Records the closing float cash movement for cash management.
   *
   * @param {Number} closingFloatAmount
   *        The amount of the closing float to be registered.
   *
   * @return {Promise} Promise resolved when the closing float has been set.
   */
  public recordClosingFloat(
    closingFloatAmount: number | string
  ): IPromise<unknown> {
    const cashMovementData = {
      amount: closingFloatAmount,
      type: CASH_MOVEMENT_TYPES.CLOSING_CASH_FLOAT,
    }
    $log.debug(
      `[CASH MOVEMENTS MANAGER] Closing float: ${cashMovementData.amount}`
    )
    return this.recordCashMovement(cashMovementData)
  }

  /**
   * Manages possible duplicate requests for setting a cash movemement type which should only be set once during a
   * registers opening sequence. If the error status code is a 400 and the request is for a unique movement type,
   * retrieve the cash movements for that type and determine if it has already been set.
   *
   * @param cashMovementData {Object}
   *        The cash movement data.
   *
   * @param  error {Object}
   *         The error return from the api
   *
   * @return {Promise} Resolves if the cash movemement type has already been set for the register.
   */
  private _isDuplicateMovementRequest(
    cashMovementData: $TSFixMe,
    error: $TSFixMe
  ): IPromise<unknown> {
    return $q((resolve, reject) => {
      const logAndReject = () => {
        trackError('[CashMovementsManager] Error recording a cash movement', {
          data: { cashMovementData, error },
        })
        reject(error)
      }

      if (
        error.status === 400 &&
        includes(UNIQUE_TYPES, cashMovementData.type)
      ) {
        this.getCashMovements({ type: cashMovementData.type })
          .then(data => {
            if (data.length) {
              resolve()
            } else {
              logAndReject()
            }
          })
          .catch(() => logAndReject())
      } else {
        logAndReject()
      }
    })
  }
}
export const cashMovementsManager = new CashMovementsManager()

angular
  .module('webregister.managers')
  .factory('cashMovementsManager', () => cashMovementsManager)
